import { usStatesValues } from 'constants/enums/usStates';
import { usTerritoriesPVQValues } from 'constants/enums/usTerritories';
import { militaryStatesValues } from 'constants/enums/militaryStates';
import postOfficeCities from 'constants/enums/postOfficeCities';

import { isPO, isUS, isInternational } from 'helpers/location';
import {
  zipCodePattern, notPOBoxPattern, cityPattern, installationNamePattern
} from 'constants/patterns';
import { countries } from 'config/locales/en/countries';
import { ValidateJSModel, LocationModel, ValidateJSContext } from 'forms/shared/types';
import { ValidateJSInclusionBaseValidatorOptions } from 'forms/shared/types/validatejs/inclusion';
import { REQUIRED } from 'constants/errors';
import { COUNTRY_FOREIGN, COUNTRY_POSTOFFICE } from 'constants/enums/locations';
import { countryValuesNoUS } from 'constants/enums/countries';
import { isPVQForm } from 'helpers/form';
import { hasYesOrNo } from 'models/validate';
import { isPostalZipcode } from 'config/zipcodes';
import { DeepSafePartial } from 'types/types';

type Attributes = DeepSafePartial<LocationModel>;

type CountryKeys = keyof typeof countries

const pvqLocationModelValidator: ValidateJSModel<LocationModel> = {
  countryComments: {},

  layout: {},

  street2: (value, attributes, attributeName, options) => {
    if (!options?.allowPOBox) {
      return {
        presence: false,
        format: { pattern: notPOBoxPattern, flags: 'i', message: 'error.location.street.poBox.pattern' },
      };
    }

    return {
      presence: false,
    };
  },

  validated: {},

  street: (value, attributes: Attributes, attributeName, options) => {
    if (options?.requireStreet) {
      if (attributes?.streetUnknown?.applicable === false) {
        return {};
      }

      if (options?.allowPOBox) {
        return { presence: true };
      }

      return {
        presence: true,
        format: { pattern: notPOBoxPattern, flags: 'i', message: 'error.location.street.poBox.pattern' },
      };
    }
    return {};
  },

  city: (value, attributes: Attributes, attributeName, options) => {
    if (attributes?.cityUnknown?.applicable === false) {
      return {};
    }

    if (options?.requireCity === false) {
      return {};
    }

    if (isPO(attributes) || options?.onlyMilitaryAddress === true) {
      return {
        presence: true,
        inclusion: {
          within: postOfficeCities,
          message: 'error.city.apofporequired',
        },
      };
    }

    return {
      presence: true,
      format: { pattern: cityPattern, message: 'error.city.pattern' },
      length: {
        minimum: 2,
        maximum: 100,
        message: 'error.city.length',
      },
    };
  },

  state: (value, attributes: Attributes, attributeName, options) => {
    if (attributes?.stateUnknown?.applicable === false || options?.requireState === false) {
      return {};
    }

    if (isPO(attributes) || options?.onlyMilitaryAddress === true) {
      return {
        presence: true,
        inclusion: { within: militaryStatesValues, message: 'error.state.notfound' },
      };
    }

    if (isUS(attributes) || (!attributes?.country && attributes?.countryNotApplicable?.applicable === true)) { // If country explicityly U.S. OR if country is not set to any value
      return {
        presence: true,
        inclusion: {
          within: [...usStatesValues, ...usTerritoriesPVQValues],
          message: 'error.state.notfound',
        },
      };
    }

    return {};
  },

  zipcode: (value, attributes: Attributes, attributeName, options) => {
    if ((attributes?.zipcodeUnknown?.applicable === false) || (options?.requireZipCode === false)) {
      return {};
    }

    if (!isInternational(attributes)) {
      return {
        presence: true,
        format: { pattern: zipCodePattern, message: 'error.zipcode.pattern' },
        zipcode: true, // zipcode + state validator
      };
    }

    return {};
  },

  country: (value, attributes: Attributes, attributeName, options) => {
    if (attributes?.countryFocused || attributes?.countryNotApplicable?.applicable === false) {
      return {};
    }
    const inclusion: ValidateJSInclusionBaseValidatorOptions = options?.excludeUnitedStates ? {
      within: countryValuesNoUS,
      message: 'error.country.notfound',
    } : {
      within: Object.keys(countries).map((key) => countries[key as CountryKeys].value),
      message: 'error.country.notfound',
    };

    const context: ValidateJSContext<any, any> = {
      presence: true,
      inclusion,
    };
    if (options?.onlyMilitaryAddress === true) {
      inclusion.within = [COUNTRY_POSTOFFICE];
      inclusion.message = 'error.country.pomissing';

      return context;
    }
    // This handles the value 'INTERNATIONAL'. Basically, when this value is
    // set, the user is still required to input a country, thus, we return a
    // required error message here.
    if (typeof value === 'string' && value === COUNTRY_FOREIGN) { return { exclusion: { within: [COUNTRY_FOREIGN], message: REQUIRED } }; }

    if (typeof value === 'object' && value.value === COUNTRY_FOREIGN) {
      return {
        hasValue: {
          validator: {
            exclusion: { within: [COUNTRY_FOREIGN], message: REQUIRED },
          },
        },
      };
    }

    if (options?.militaryAddress === true) {
      inclusion.within.push(COUNTRY_POSTOFFICE);

      return context;
    }

    if (options?.militaryAddress === false) {
      context.exclusion = { within: [COUNTRY_POSTOFFICE], message: 'error.country.ponotallowed' };

      if (value === COUNTRY_POSTOFFICE) {
        context.inclusion = {};
      }

      return context;
    }

    return context;
  },

  county: (value, attributes, attributeName, options) => {
    if (isPVQForm(options?.formType)) {
      if (
        options.requireCounty === true
        || (!attributes.city && options.requireCityOrCounty)
      ) {
        if (!isInternational(attributes)) {
          return { presence: true };
        }
      } return {};
    }

    if (!isInternational(attributes)) {
      return { presence: true };
    }

    return {};
  },

  isMilitaryDiplomaticAddress: (value, attributes, attributeName, options) => (options?.requireIsMilitaryDiplomaticAddress
    ? { hasValue: { validator: hasYesOrNo } } : {}),

  militaryDiplomaticInstallationName: (value, attributes: Attributes, attributeName, options) => {
    if (options?.requireMilitaryDiplomaticInstallationName && attributes?.isMilitaryDiplomaticAddress?.value === 'Yes' && attributes?.militaryInstallationNameNotApplicable?.applicable !== false) {
      return {
        format: {
          message: 'error.pvq.installationName.pattern',
          pattern: installationNamePattern,
        },
        length: {
          maximum: 100,
          message: 'error.pvq.installationName.length',
          minimum: 2,
        },
        presence: true
      };
    }

    return {};
  },

  militaryDiplomaticInstallationNameExplanation: (value, attributes: Attributes, attributeName, options) => {
    if (
      options?.requireMilitaryDiplomaticInstallationNameExplanation
      && options?.requireMilitaryDiplomaticInstallationName
      && attributes?.isMilitaryDiplomaticAddress?.value === 'Yes'
      && attributes?.militaryInstallationNameNotApplicable?.applicable === false
    ) {
      return {
        presence: true,
      };
    }

    return {};
  },

  apoFpoDpoPostcode: (value, attributes: Attributes, attributeName, options) => {
    if (options?.requireApoFpoDpoPostcode && attributes?.isMilitaryDiplomaticAddress?.value === 'Yes' && attributes?.apoFpoNotApplicable?.applicable !== false) {
      if (!isPostalZipcode(value) && value !== '') { // Check if zipcode is a valid APOFPODPO zipcode
        return {
          presence: true,
          format: { pattern: zipCodePattern, message: 'error.pvq.zipcode.pattern' },
          requireTrue: { // Force error message; value will never be true
            message: 'error.pvq.zipcode.state'
          }
        };
      }
      return {
        presence: true,
        format: { pattern: zipCodePattern, message: 'error.pvq.zipcode.pattern' },
      };
    }

    return {};
  }

};

export default pvqLocationModelValidator;
